import { Actions } from '@ngrx/effects';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { EffectsBase } from '../../../core/store/effects-base';
import { TasksService } from '../../../core/services/tasks.service';
import {
  addComment,
  addComment_Success,
  changeTaskStatus,
  changeTaskStatus_Failure,
  changeTaskStatus_Success,
  fetchTaskDetails,
  fetchTaskDetails_Success,
  fetchTasks,
  fetchTasks_Failure,
  fetchTasks_Success,
  fetchTaskStatuses,
  fetchTaskStatuses_Success,
  markTasksAsReviewed,
  markTasksAsReviewed_Success,
  setTaskDetailsCommentFailed,
  setTaskDetailsCommentPending,
  toggleSidebar,
} from './tasks.actions';
import { TranslateService } from '@ngx-translate/core';
import { NotificationType } from '../../../core/interfaces';
import {
  SystemMessagesService,
  NotificationService,
  ToastrAbstractionService,
} from '../../../core/services';

@Injectable()
export class TasksEffects extends EffectsBase {
  fetchTasks$ = this.createEffect(
    fetchTasks,
    switchMap(({ controlId, homeTasks }) => {
      return this.tasksService.getTasks(controlId, homeTasks).pipe(
        switchMap((tasks) => {
          return [fetchTasks_Success({ tasks })];
        }),
        catchError(() => [fetchTasks_Failure()]),
      );
    }),
  );

  changeTaskStatus$ = this.createEffect(
    changeTaskStatus,
    mergeMap(
      ({ taskId, status, successFnc, failFnc, outputText, controlId }) => {
        this.notificationService.trackUserAction(
          `${NotificationType.DisoTasks}_${taskId}`,
        );
        return this.tasksService
          .updateTaskStatus({ taskId, status, outputText })
          .pipe(
            switchMap((task) => {
              successFnc && successFnc();
              return [
                changeTaskStatus_Success({ taskId, task }),
                fetchTasks({ bypassLoader: true }),
              ];
            }),
            catchError(() => {
              failFnc && failFnc();
              return [changeTaskStatus_Failure({ taskId, status })];
            }),
          );
      },
    ),
  );

  fetchTaskStatuses$ = this.createEffect(
    fetchTaskStatuses,
    switchMap(() => {
      return this.tasksService.getTaskStatuses().pipe(
        switchMap((statuses) => {
          return [fetchTaskStatuses_Success({ taskStatuses: statuses })];
        }),
      );
    }),
  );

  markTasksAsReviewed$ = this.createEffect(
    markTasksAsReviewed,
    switchMap(({ controlId }) => {
      return this.tasksService.markTasksAsReviewed(controlId).pipe(
        switchMap(() => {
          return [markTasksAsReviewed_Success()];
        }),
      );
    }),
  );

  fetchTaskDetails$ = this.createEffect(
    fetchTaskDetails,
    switchMap(({ taskId }) => {
      return this.tasksService.fetchTaskDetails(taskId).pipe(
        switchMap((taskDetails) => {
          return [fetchTaskDetails_Success({ taskDetails })];
        }),
        catchError(() => {
          return [fetchTasks_Failure(), toggleSidebar({ showSidebar: false })];
        }),
      );
    }),
  );

  addComment$ = this.createEffect(
    addComment,
    switchMap(({ commentText, taskId }) => {
      return this.tasksService.addComment(taskId, commentText).pipe(
        switchMap((comment) => {
          return [
            addComment_Success({ comment }),
            setTaskDetailsCommentPending({ pending: false }),
          ];
        }),
        catchError((error) => {
          const errorMessage = this.systemErrorsService.systemMessages.find(
            (err) => err.id === error.message,
          )?.message;
          return [
            setTaskDetailsCommentFailed({ failed: true, errorMessage }),
            setTaskDetailsCommentPending({ pending: false }),
          ];
        }),
      );
    }),
  );

  constructor(
    private tasksService: TasksService,
    private toastService: ToastrAbstractionService,
    private translateService: TranslateService,
    private notificationService: NotificationService,
    private systemErrorsService: SystemMessagesService,
    override actions$: Actions,
  ) {
    super(actions$);
  }
}
